import React, { useEffect, useState } from 'react';
import { Phone, PlusCircle, X, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';
import ApkReleaseCard from './ApkReleaseCard';
import CompAccessAdminCard from './CompAccessAdminCard';

function fmtDays(iso) {
  if (!iso) return '—';
  const days = Math.round((new Date(iso) - Date.now()) / 86400000);
  if (days < 0) return 'expired';
  if (days === 0) return 'today';
  return `${days}d left`;
}

// FastAPI errors surface at `response.data.detail`, but `detail` can be a
// plain string OR the dict we raise in `admin_voice_numbers.py`
// (`{"error": "twilio_provision_failed", "message": "…"}`). We also want the
// HTTP status when the backend hasn't returned any JSON body (e.g. 502 from
// an ingress). Without this helper the UI showed just "Unknown".
function describeError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  let body = '';
  if (typeof detail === 'string') body = detail;
  else if (detail && typeof detail === 'object') {
    body = detail.message || detail.error || JSON.stringify(detail);
  } else if (e?.message) body = e.message;
  const prefix = status ? `HTTP ${status}` : 'Network error';
  return body ? `${prefix} — ${body}` : prefix;
}

export default function CrmAdminVoiceNumbers() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', days: 21 });
  const [promoteEmail, setPromoteEmail] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/admin/voice-numbers');
      setRows(data.rows || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: describeError(e), variant: 'destructive' });
    }
  };

  useEffect(() => { load(); }, []);

  const grant = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/voice-numbers/provision', form);
      toast({ title: `Granted ${data.assistant_number} to ${data.email} for ${data.days}d` });
      setForm({ email: '', days: 21 });
      await load();
    } catch (e) {
      toast({ title: 'Grant failed', description: describeError(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const release = async (email) => {
    if (!window.confirm(`Release the number for ${email}?`)) return;
    try {
      await api.post('/admin/voice-numbers/release', { email });
      toast({ title: `Released ${email}` });
      await load();
    } catch (e) {
      toast({ title: 'Release failed', description: describeError(e), variant: 'destructive' });
    }
  };

  const extend = async (email, additionalDays = 7) => {
    // Uses the dedicated /admin/voice-numbers/extend endpoint — pushes
    // the auto-release date out WITHOUT re-provisioning the number, so
    // it stays live and Twilio doesn't get charged for a second SID.
    try {
      const { data } = await api.post('/admin/voice-numbers/extend', {
        email, additional_days: additionalDays,
      });
      toast({ title: `Extended ${email} by ${additionalDays}d — new expiry ${data.expires_at?.slice(0, 10)}` });
      await load();
    } catch (e) {
      toast({ title: 'Extend failed', description: describeError(e), variant: 'destructive' });
    }
  };

  const promote = async () => {
    if (!promoteEmail) return;
    try {
      const { data } = await api.post('/admin/users/promote', { email: promoteEmail });
      toast({ title: data.already_admin ? `${data.email} is already admin` : `Promoted ${data.email}` });
      setPromoteEmail('');
    } catch (e) {
      toast({ title: 'Promote failed', description: describeError(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-voice-numbers">
      <ApkReleaseCard />
      <CompAccessAdminCard />
      <div>
        <h2 className="font-serif text-2xl text-brand-green">Admin — voice numbers</h2>
        <p className="text-sm text-brand-green/70 mt-1">Grant a Twilio AI number to any user for a set period. The hourly loop auto-releases expired grants.</p>
      </div>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-brand-green">
          <PlusCircle className="w-4 h-4 text-brand-gold" />
          <div className="text-sm font-semibold">Grant a new number</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3">
          <div>
            <Label htmlFor="grant-email">User email</Label>
            <Input id="grant-email" data-testid="grant-email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   placeholder="user@example.com" />
          </div>
          <div>
            <Label htmlFor="grant-days">Days</Label>
            <Input id="grant-days" data-testid="grant-days" type="number" min="1" max="365"
                   value={form.days}
                   onChange={(e) => setForm({ ...form, days: Number(e.target.value) || 21 })} />
          </div>
          <div className="flex items-end">
            <Button onClick={grant} disabled={busy || !form.email} data-testid="grant-submit"
                    className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
              {busy ? 'Granting…' : 'Grant'}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center gap-2 text-brand-green">
          <ShieldCheck className="w-4 h-4 text-brand-gold" />
          <div className="text-sm font-semibold">Promote a user to admin</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <Input data-testid="promote-email" value={promoteEmail}
                 onChange={(e) => setPromoteEmail(e.target.value)}
                 placeholder="listingaigent@gmail.com" />
          <Button onClick={promote} disabled={!promoteEmail} data-testid="promote-submit"
                  className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
            Promote
          </Button>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3 text-brand-green">
          <Phone className="w-4 h-4 text-brand-gold" />
          <div className="text-sm font-semibold">Active grants ({rows.length})</div>
        </div>
        {rows.length === 0 && <div className="text-xs text-brand-green/60">No numbers currently granted.</div>}
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} data-testid={`voice-row-${r.id}`}
                 className="flex items-center justify-between gap-3 border-b border-brand-cream last:border-0 py-3">
              <div className="min-w-0">
                <div className="font-medium text-brand-green truncate">{r.email}</div>
                <div className="text-xs text-brand-green/60 font-mono">{r.assistant_number}</div>
                <div className="text-[11px] text-brand-green/60 flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" /> {fmtDays(r.assistant_number_expires_at)}
                  {r.assistant_number_provisioning_kind === 'admin_grant' && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-brand-cream text-brand-green/80">
                      granted by {r.assistant_number_provisioned_by || 'admin'}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => extend(r.email, 7)}
                        data-testid={`extend-${r.id}`}>+7d</Button>
                <Button size="sm" variant="ghost" onClick={() => release(r.email)}
                        data-testid={`release-${r.id}`}
                        className="text-red-700 hover:bg-red-50">
                  <X className="w-3 h-3 mr-1" /> Release
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
